import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRankingStar,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { AnalyticsWidgetUiDetailsDto } from "../../../types/analytics";
import { UsageExplorerViewToken } from "../../../types/analytics/UsageExplorerViewToken";
import UsageFeatureTeaserCard from "./UsageFeatureTeaserCard";
import UsageFocusPanel from "./UsageFocusPanel";
import UsageLeagueTable from "./UsageLeagueTable";

type UsageExpandableFeature = "FOCUS" | "LEAGUE";

interface UsageFeatureSectionsProps {
  filters: AnalyticsFilterInputs;
  widget: AnalyticsWidgetUiDetailsDto;
  explorerViewToken: UsageExplorerViewToken | null;
  onExplorerViewChange: (token: UsageExplorerViewToken) => void;
}

function UsageFeatureSections({
  filters,
  widget,
  explorerViewToken,
  onExplorerViewChange,
}: UsageFeatureSectionsProps) {
  const { t } = useTranslation();
  const [expandedFeatures, setExpandedFeatures] = useState<
    UsageExpandableFeature[]
  >([]);

  const expandFeature = (feature: UsageExpandableFeature) => {
    setExpandedFeatures((current) =>
      current.includes(feature) ? current : [...current, feature],
    );
  };

  const remainingFeatures = (
    ["FOCUS", "LEAGUE"] as UsageExpandableFeature[]
  ).filter((feature) => !expandedFeatures.includes(feature));

  return (
    <>
      {expandedFeatures.map((feature) =>
        feature === "FOCUS" ? (
          <UsageFocusPanel
            key={feature}
            filters={filters}
            explorerViewToken={explorerViewToken}
            onExplorerViewChange={onExplorerViewChange}
            generateOnMount
          />
        ) : (
          <UsageLeagueTable
            key={feature}
            filters={filters}
            widget={widget}
          />
        ),
      )}

      {remainingFeatures.length > 0 && (
        <div className="mx-3 mt-8 grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fit,minmax(18rem,1fr))]">
          {remainingFeatures.map((feature) => (
            <div
              key={feature}
              className={
                remainingFeatures.length === 1
                  ? "h-full w-full md:max-w-[calc(50%_-_0.5rem)]"
                  : "h-full w-full"
              }
            >
              {feature === "FOCUS" ? (
                <UsageFeatureTeaserCard
                  title={t(
                    "Analytics.Widgets.Usage.Focus.TeaserHeading",
                    "Improve completion with AI",
                  )}
                  description={t(
                    "Analytics.Widgets.Usage.Focus.TeaserDescription",
                    "Analyses the current figures and suggests which managers or locations to chase first.",
                  )}
                  preview={<FocusTeaserPreview />}
                  actionLabel={t(
                    "Analytics.Widgets.Usage.Focus.TeaserAction",
                    "Suggest where to focus",
                  )}
                  actionIcon={
                    <FontAwesomeIcon
                      icon={faWandMagicSparkles}
                      aria-hidden
                    />
                  }
                  onAction={() => expandFeature("FOCUS")}
                />
              ) : (
                <UsageFeatureTeaserCard
                  title={t(
                    "Analytics.Widgets.Usage.League.Heading",
                    "League table",
                  )}
                  description={t(
                    "Analytics.Widgets.Usage.League.Description",
                    "Compare completion rates across locations or job levels.",
                  )}
                  preview={<LeagueTeaserPreview />}
                  actionLabel={t(
                    "Analytics.Widgets.Usage.League.Show",
                    "Show league table",
                  )}
                  actionIcon={
                    <FontAwesomeIcon icon={faRankingStar} aria-hidden />
                  }
                  onAction={() => expandFeature("LEAGUE")}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function FocusTeaserPreview() {
  return (
    <div className="grid w-full grid-cols-[minmax(0,1fr)_7rem] items-center gap-4">
      <div className="space-y-2">
        <div className="h-2.5 w-full rounded bg-gray-300" />
        <div className="h-2.5 w-5/6 rounded bg-gray-200" />
        <div className="h-2.5 w-2/3 rounded bg-gray-200" />
      </div>
      <div className="rounded-md border border-gray-300 bg-white p-3">
        <div className="h-3 w-10 rounded-full bg-gray-300" />
        <div className="mt-3 h-2 w-full rounded bg-gray-200" />
        <div className="mt-2 h-2 w-4/5 rounded bg-gray-200" />
        <div className="mt-3 h-4 w-14 rounded bg-gray-300" />
      </div>
    </div>
  );
}

function LeagueTeaserPreview() {
  return (
    <div className="w-full space-y-2.5">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="grid grid-cols-[1.5rem_minmax(0,1fr)_4rem] items-center gap-2"
        >
          <div className="h-5 w-5 rounded-full border border-gray-300 bg-white" />
          <div className="h-2 rounded bg-gray-200">
            <div
              className="h-full rounded bg-gray-400"
              style={{ width: `${80 - index * 18}%` }}
            />
          </div>
          <div className="h-2 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

export default UsageFeatureSections;
